<script setup>
import { ref } from 'vue';
import AppMenuItem from './AppMenuItem.vue';
import ShortLinkCreate from '../views/ShortLink.vue';
import PageCreate from '../views/Page.vue';
import WebformCreate from '../views/Webform.vue';
import BulkCreate from '../views/Bulk.vue';
import QrCodeCreate from '../components/QrCodeCreate.vue';
import ExtensionsDialog from '../views/Extensions.vue';
import SupportDialog from '../views/Support.vue';
import { useI18n } from 'vue-i18n';
import { useLayout } from '@/layout/composables/layout';

const { t } = useI18n();
const { layoutConfig, toggleSidebarCollapse } = useLayout();

const newMenu = ref();

function toggleNewMenu(event) {
  newMenu.value.toggle(event);
}

const separator = ref(false);

const drawerVisibleShortlink = ref(false);

function openDrawerShortlink() {
  drawerVisibleShortlink.value = true;
}

function updateDrawerVisibilityShortlink(value) {
  drawerVisibleShortlink.value = value;
}

const drawerVisiblePage = ref(false);

function openDrawerPage() {
  drawerVisiblePage.value = true;
}

function updateDrawerVisibilityPage(value) {
  drawerVisiblePage.value = value;
}

const drawerVisibleWebform = ref(false);

function openDrawerWebform() {
  drawerVisibleWebform.value = true;
}

function updateDrawerVisibilityWebform(value) {
  drawerVisibleWebform.value = value;
}

const drawerVisibleBulk = ref(false);

function openDrawerBulk() {
  drawerVisibleBulk.value = true;
}

function updateDrawerVisibilityBulk(value) {
  drawerVisibleBulk.value = value;
}

const drawerVisibleQrcode = ref(false);

function openDrawerQrcode() {
  drawerVisibleQrcode.value = true;
}

function updateDrawerVisibilityQrcode(value) {
  drawerVisibleQrcode.value = value;
}

function onQrcodeDynamic() {
  drawerVisibleQrcode.value = false;
  openDrawerShortlink();
}

const dialogVisibleExtensions = ref(false);

function openDialogExtenions() {
  dialogVisibleExtensions.value = true;
}

function dialogVisibilityExtensions(value) {
  dialogVisibleExtensions.value = value;
}

const dialogVisibleSupport = ref(false);

function openDialogSupport() {
  dialogVisibleSupport.value = true;
}

function dialogVisibilitySupport(value) {
  dialogVisibleSupport.value = value;
}

const model = ref([
  {
    items: [
      { separator: true },
      { label: 'menu.dashboard', icon: 'pi pi-fw pi-home', to: '/' },
      { separator: true },
      { label: 'menu.shortlinks', icon: 'pi pi-fw pi-link', to: '/shortlinks' },
      { label: 'menu.qrcodes', icon: 'pi pi-fw pi-qrcode', to: '/qrcodes' },
      { label: 'menu.pages', icon: 'pi pi-fw pi-file', to: '/pages', class: 'rotated-icon' },
      { label: 'menu.webforms', icon: 'pi pi-fw pi-clipboard', to: '/webforms', class: 'rotated-icon' },
      { separator: true }
    ],
  },
  {
    items: [
      { label: 'menu.domains', icon: 'pi pi-fw pi-globe', to: '/domains' },
      { label: 'menu.workspaces', icon: 'pi pi-fw pi-users', to: '/workspaces' },
      { label: 'menu.email_reports', icon: 'pi pi-fw pi-inbox', to: '/scheduled' },
    ],
  },
]);
const items = [
  {
    label: t('menu.shortlink'),
    icon: 'pi pi-link',
    command: () => {
      openDrawerShortlink();
    }
  },
  {
    separator: true
  },
  {
    label: t('menu.bulks'),
    icon: 'pi pi-file-import',
    command: () => {
      openDrawerBulk();
    }
  },
  {
    separator: true
  },
  {
    label: t('menu.qrcode'),
    icon: 'pi pi-qrcode',
    command: () => {
      openDrawerQrcode();
    },
  },
  {
    separator: true
  },
  {
    label: t('menu.page'),
    icon: 'pi pi-file',
    command: () => {
      openDrawerPage();
    },
  },
  {
    separator: true
  },
  {
    label: t('menu.webform'),
    icon: 'pi pi-clipboard',
    command: () => {
      openDrawerWebform();
    },
  }
];

const modelFooter = ref([
  {
    items: [
      {
        label: t('menu.extensions'), icon: 'pi pi-fw pi-compass', command: () => {
          openDialogExtenions();
        },
      },
      {
        separator: true
      },
      {
        label: t('menu.support'), icon: 'pi pi-fw pi-question', command: () => {
          openDialogSupport();
        },
      }
    ],
  },
]);

</script>

<template>
  <ul class="layout-menu">
    <ShortLinkCreate :visible="drawerVisibleShortlink" @update:visible="updateDrawerVisibilityShortlink" />
    <PageCreate :visible="drawerVisiblePage" @update:visible="updateDrawerVisibilityPage" />
    <WebformCreate :visible="drawerVisibleWebform" @update:visible="updateDrawerVisibilityWebform" />
    <BulkCreate :visible="drawerVisibleBulk" @update:visible="updateDrawerVisibilityBulk" />
    <QrCodeCreate :visible="drawerVisibleQrcode" @update:visible="updateDrawerVisibilityQrcode" @dynamic="onQrcodeDynamic" />
    <ExtensionsDialog :visible="dialogVisibleExtensions" @update:visible="dialogVisibilityExtensions" />
    <SupportDialog :visible="dialogVisibleSupport" @update:visible="dialogVisibilitySupport" />

    <!-- "New" opens the creation menu (short link, bulk, QR code, ...). -->
    <Button v-if="!layoutConfig.sidebarCollapsed" :fluid="true" class="mb-2 mt-1" size="large"
      :label="t('common.labels.new')" icon="pi pi-chevron-down" iconPos="right" pt:label:class="flex-1"
      @click="toggleNewMenu"
      aria-haspopup="true" aria-controls="new_menu" />
    <div v-else class="flex justify-center mb-2 mt-1">
      <Button icon="pi pi-plus-circle" size="large" @click="toggleNewMenu" aria-haspopup="true"
        aria-controls="new_menu" v-tooltip.right="t('common.labels.new')" :aria-label="t('common.labels.new')" />
    </div>
    <Menu ref="newMenu" id="new_menu" :model="items" :popup="true" pt:list:class="p-2! gap-2!" pt:item:class="px-0.5 py-1"
      pt:itemLink:class="px-4! py-2.5!" />

    <div v-if=separator class="border-t-[0.1rem] mb-5 mt-5"></div>
    <template v-for="(item, i) in model" :key="item">
      <app-menu-item v-if="!item.separator" :item="item"></app-menu-item>
    </template>
  </ul>
  <ul class="layout-menu">
    <template v-for="(item, i) in modelFooter" :key="item">
      <app-menu-item v-if="!item.separator" :item="item"></app-menu-item>
    </template>
    <li class="layout-sidebar-collapse-item hidden lg:block">
      <div class="border-t-[0.1rem] mb-5 mt-5"></div>
      <ul>
        <li>
          <a @click="toggleSidebarCollapse" tabindex="0"
            v-tooltip.right="layoutConfig.sidebarCollapsed ? t('menu.expand') : ''">
            <i class="layout-menuitem-icon pi pi-fw"
              :class="layoutConfig.sidebarCollapsed ? 'pi-angle-double-right' : 'pi-angle-double-left'" />
            <span class="layout-menuitem-text">{{ t('menu.collapse') }}</span>
          </a>
        </li>
      </ul>
    </li>
  </ul>
</template>
